import type { CivilLitigationWizardDraft } from '../../../types/civilLitigationCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import {
  contactNumberError,
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
  todayIsoDateLocal,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import type { CivilLitigationWizardStep } from './civilLitigationConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { appendInstituteValidationSpecs } from '../../../utils/instituteTypeValidation';
import { isHighUrgencyLevel, isCivilLitigationOriginalCourtsCategory, requiresCourtType } from './civilLitigationHelpers';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validateCivilLitigationStep(
  step: CivilLitigationWizardStep,
  draft: CivilLitigationWizardDraft,
  fileCategories: LookupItem[] = [],
  corporateInstitutes: LookupItem[] = [],
  subCategories: LookupItem[] = [],
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'clit-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'clit-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'clit-description' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'clit-province' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'clit-yourRef' },
      { key: 'hasActionInstituted', label: 'Has action been instituted?', value: draft.hasActionInstituted, elementId: 'clit-actionInstituted' },
      { key: 'caseNumber', label: 'Case Number', value: draft.caseNumber, elementId: 'clit-caseNumber' },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'clit-connected' },
    );

    appendInstituteValidationSpecs(specs, draft, draft.fileCategoryId, fileCategories, 'clit', corporateInstitutes);

    if (requiresCourtType(draft.hasActionInstituted, draft.subFileCategoryId, subCategories, draft.subFileCategoryName)) {
      specs.push({
        key: 'courtTypeId',
        label: 'Court Type',
        value: draft.courtTypeId,
        elementId: 'clit-courtType',
      });
    }

    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: 'Connected Case Number', value: draft.connectedCaseNumber, elementId: 'clit-connectedCase' },
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push(
          { key: 'connectedAgdFileRef', label: 'Connected AGD File Reference Number', value: draft.connectedAgdFileRef, elementId: 'clit-connectedAgd' },
        );
      }

    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
    if (exceedsMax(draft.courtTypeOther, MAX_REF)) extra.courtTypeOther = maxCharLimitExceededError();
    // if (exceedsMax(draft.corporateInstituteOther, MAX_REF)) extra.corporateInstituteOther = maxCharLimitExceededError();
  }

  if (step >= 2) {
    const isOriginalCourts = isCivilLitigationOriginalCourtsCategory(
      draft.fileCategory,
      draft.subFileCategoryName,
    );

    specs.push(
      { key: 'plaintiffName', label: 'Plaintiff / Applicant Name', value: draft.plaintiffName, elementId: 'clit-plaintiff' },
      { key: 'defendantName', label: 'Defendant / Respondent Name', value: draft.defendantName, elementId: 'clit-defendant' },
      {
        key: 'partyBodyType',
        label: 'Select one of the following',
        value: draft.involvesGovernmentBody === 'yes' || draft.involvesPublicCorporation === 'yes' ? 'selected' : '',
        elementId: 'clit-partyBodyType',
      },
      { key: 'legalOfficerName', label: 'Name of the Legal Officer/ Officer In-Charge', value: draft.legalOfficerName, elementId: 'clit-legalOfficer' },
      { key: 'legalOfficerContact', label: 'Contact Number', value: draft.legalOfficerContact, elementId: 'clit-legalContact' },
    );

    if (draft.involvesPublicCorporation === 'yes' && !isOriginalCourts) {
      const hasCorp = draft.publicCorporationNames.some((n) => n.trim());
      if (!hasCorp) extra.publicCorporationNames = 'Add at least one public corporation name.';
    }

    if (!isOriginalCourts) {
      draft.publicCorporationNames.forEach((name, i) => {
        if (exceedsMax(name, MAX_REF)) extra[`publicCorp-${i}`] = maxCharLimitExceededError();
      });
    }

    if (exceedsMax(draft.plaintiffName, MAX_REF)) extra.plaintiffName = maxCharLimitExceededError();
    if (exceedsMax(draft.defendantName, MAX_REF)) extra.defendantName = maxCharLimitExceededError();
    if (exceedsMax(draft.otherInvolvedInstitutions, MAX_DESC)) extra.otherInvolvedInstitutions = maxCharLimitExceededError();
    if (exceedsMax(draft.legalOfficerName, MAX_REF)) extra.legalOfficerName = maxCharLimitExceededError();

    const contactErr = contactNumberError(draft.legalOfficerContact);
    if (contactErr) extra.legalOfficerContact = contactErr;
  }

  if (step >= 3) {
    specs.push(
      { key: 'subjectOfDispute', label: 'Subject of the Dispute', value: draft.subjectOfDispute, elementId: 'clit-subject' },
      { key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'clit-urgency' },
    );

    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'clit-urgencyJustification',
      });
    }

    if (exceedsMax(draft.subjectOfDispute, MAX_DESC)) extra.subjectOfDispute = maxCharLimitExceededError();
    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (exceedsMax(draft.nextStepOfCaseOther, MAX_REF)) extra.nextStepOfCaseOther = maxCharLimitExceededError();
    if (draft.nextCaseDate && !isTodayOrFutureIsoDate(draft.nextCaseDate)) {
      extra.nextCaseDate = validationDateFutureOnly();
    }
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 4) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'clit-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'clit-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 5) {
    Object.assign(extra, validateStep5Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep5Documents(draft: CivilLitigationWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if ((doc.files ?? []).length > 0) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export function minDeadlineDateForCivilLitigation(): string {
  return todayIsoDateLocal();
}
